import { parse } from 'query-string';
import type { Feature } from '../../../../types/CompanyDtos';
import { getFullKey } from '../LocalStorage';

const allowedLocalOverrides = {
    testNewInbox: 'FEATURE_inboxPage',
    testNewObjectiveListView: 'FEATURE_objectiveNewListView',
    testReviewsAnomalyPremium: 'FEATURE_reviewOverviewTableAnomalyPremium',
    testReviewsAnomalyRatedAnswers: 'FEATURE_reviewOverviewTableAnomalyRatedAnswers',
    testReviewsResultColumns: 'FEATURE_reviewOverviewTableResultColumns',
    dataTableGroupingSetIntermediate: 'FEATURE_dataTableGroupingSetIntermediate',
    dataTableGroupingSetFull: 'FEATURE_dataTableGroupingSetFull',
} satisfies Record<string, Feature>;

export type LocalOverride = (typeof allowedLocalOverrides)[keyof typeof allowedLocalOverrides];

export const getHasLocalOverride = () => {
    const localStorageKey = getFullKey('local-feature-override');
    const allowedFeatures = Object.values(allowedLocalOverrides);
    const query = parse(window.location.search);

    const featuresToToggle = Object.entries(allowedLocalOverrides).reduce<Partial<{ [feature in Feature]: boolean }>>(
        (acc, [key, feature]) => {
            if (query[key] === 'true') {
                acc[feature] = true;
            } else if (query[key] === 'false') {
                acc[feature] = false;
            }
            return acc;
        },
        {}
    );

    // There might be contexts where local storage is not available.
    // Since this is used everywhere we want to play it extra safe.
    try {
        const localStorageItem = window.localStorage.getItem(localStorageKey);
        const currentOverrides = (localStorageItem ?? '')
            .split(',')
            .filter((item): item is Feature => allowedFeatures.includes(item as LocalOverride));
        const newOverrides = allowedFeatures.filter(
            feature => featuresToToggle[feature] ?? currentOverrides.includes(feature)
        );

        if (newOverrides.length) {
            window.localStorage.setItem(localStorageKey, newOverrides.join(','));
        } else if (localStorageItem !== null) {
            window.localStorage.removeItem(localStorageKey);
        }

        const featureOverrides = new Set(newOverrides);

        return (feature: LocalOverride) => featureOverrides.has(feature);
    } catch {
        return () => false;
    }
};

export function getLocalOverrideKey(feature: Feature) {
    return Object.entries(allowedLocalOverrides).find(([, f]) => f === feature)?.[0] ?? null;
}
